import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { refreshOverrides } from "../lib/overrides";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";
import { outboxCount, flushOutbox } from "../lib/offline";

const AppCtx = createContext(null);

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLang] = useState(() => localStorage.getItem("bc_lang") || "fr");
  const [aidantMode, setAidantMode] = useState(
    () => localStorage.getItem("bc_aidant_mode") === "1"
  );
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(outboxCount());
  const [patient, setPatient] = useState(null);
  const [billing, setBilling] = useState(null);
  const [profile, setProfile] = useState(() => { try { return JSON.parse(localStorage.getItem("bc_profile") || "null"); } catch { return null; } });

  const refreshProfile = useCallback(async () => {
    try { const { data } = await api.get("/profile/me"); setProfile(data); localStorage.setItem("bc_profile", JSON.stringify(data)); } catch { /* garde le cache */ }
  }, []);
  const setPays = useCallback(async (pays) => {
    const { data } = await api.put("/profile/pays", { pays }); setProfile(data); localStorage.setItem("bc_profile", JSON.stringify(data));
    if (data.langues && !data.langues.includes(localStorage.getItem("bc_lang") || "fr")) setLang(data.langue_defaut);
    return data;
  }, []);
  useEffect(() => { if (!user) { setProfile(null); localStorage.removeItem("bc_profile"); return; } refreshProfile(); }, [user, refreshProfile]);
  const has = useCallback((f) => Boolean(profile?.features?.includes(f)), [profile]);

  const refreshBilling = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/me");
      setBilling(data);
      localStorage.setItem("bc_billing", JSON.stringify(data));
    } catch (e) {
      const cached = localStorage.getItem("bc_billing");
      if (cached) setBilling(JSON.parse(cached));
    }
  }, []);

  useEffect(() => {
    if (!user) { setBilling(null); return; }
    refreshBilling();
  }, [user, refreshBilling]);
  useEffect(() => { refreshOverrides(); }, []);

  const hasFeature = useCallback((f) => Boolean(billing?.features?.includes(f)), [billing]);

  useEffect(() => { localStorage.setItem("bc_lang", lang); }, [lang]);
  useEffect(() => {
    localStorage.setItem("bc_aidant_mode", aidantMode ? "1" : "0");
  }, [aidantMode]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); flushOutbox().then(() => setPending(outboxCount())); };
    const goOffline = () => setOnline(false);
    const onOutbox = () => setPending(outboxCount());
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    window.addEventListener("bc-outbox-change", onOutbox);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("bc-outbox-change", onOutbox);
    };
  }, []);

  // Load primary patient when user authenticated (cache local pour démarrage hors-ligne)
  useEffect(() => {
    if (!user) { setPatient(null); return; }
    try { const c = JSON.parse(localStorage.getItem("bc_patient") || "null"); if (c) setPatient(c); } catch { /* ignore */ }
    (async () => {
      try {
        const { data } = await api.get("/patients/mine");
        if (data && data.length) { setPatient(data[0]); localStorage.setItem("bc_patient", JSON.stringify(data[0])); }
      } catch (e) {
        if (!localStorage.getItem("bc_patient")) setPatient(null);
      }
    })();
  }, [user]);

  const syncNow = useCallback(async () => {
    const r = await flushOutbox();
    setPending(outboxCount());
    return r;
  }, []);

  const value = useMemo(() => ({
    lang, setLang,
    aidantMode, setAidantMode,
    online, pending, syncNow,
    patient, setPatient,
    billing, refreshBilling, hasFeature,
    profile, refreshProfile, setPays, has,
  }), [lang, aidantMode, online, pending, syncNow, patient, billing, refreshBilling, hasFeature, profile, refreshProfile, setPays, has]);

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export function useApp() { return useContext(AppCtx); }
